// The Foundation's branding and files shapes (contracts/openapi.yaml: BrandSource, BrandSummary, Brand, ContrastCheck,
// BrandVersion, File, FontFamily) and the small readings of them the editor needs. Field names are the contract's.

export const BASE_COLOURS = ["primary", "secondary", "accent", "neutral"] as const;
export const STATUS_COLOURS = ["success", "warning", "danger"] as const;
export type BaseColour = (typeof BASE_COLOURS)[number];
export type StatusColour = (typeof STATUS_COLOURS)[number];
export type ColourName = BaseColour | StatusColour;
export const STEPS = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900", "950"] as const;
export type Step = (typeof STEPS)[number];
export const COLOUR_LABELS: Record<ColourName, string> = {
  primary: "Primary",
  secondary: "Secondary",
  accent: "Accent",
  neutral: "Neutral",
  success: "Success",
  warning: "Warning",
  danger: "Danger",
};

export type LogoSlot = "primary" | "primary_dark" | "mark" | "mark_dark";
export const LOGO_SLOTS: readonly LogoSlot[] = ["primary", "primary_dark", "mark", "mark_dark"];
export type Radius = "none" | "small" | "medium" | "large" | "full";
export type Density = "compact" | "comfortable" | "roomy";
export type Scale = "minor-third" | "major-third" | "perfect-fourth";

export interface Typeface {
  family: string;
  weights: number[];
}
export interface BrandSource {
  colors: Record<BaseColour, string> & Partial<Record<StatusColour, string>>;
  overrides?: Record<string, string>;
  typography?: { heading?: Typeface; body?: Typeface; scale?: Scale; base_size?: number };
  logos?: Partial<Record<LogoSlot, string>>;
  shape?: { radius?: Radius; density?: Density };
}
export type BrandState = "draft" | "published" | "archived";
export interface BrandSummary {
  id: string;
  organization_id: string;
  name: string;
  state: BrandState;
  is_default: boolean;
  revision: number;
  current_version?: number;
  theme_url?: string;
  tokens_url?: string;
  created_at: string;
  updated_at: string;
  archived_at?: string;
}
export interface ColourToken {
  $type: "color";
  $value: { colorSpace: "oklch"; components: [number, number, number]; alpha: number; hex: string };
}
export interface DesignTokens {
  color: Record<ColourName, Record<Step, ColourToken>>;
  role: Record<"light" | "dark", Record<string, ColourToken>>;
}
export interface ContrastCheck {
  mode: "light" | "dark";
  foreground: string;
  background: string;
  ratio: number;
  needed: number;
  passes: boolean;
  message?: string;
  pointer?: string;
  suggestion?: string;
}
export interface Brand extends BrandSummary {
  source: BrandSource;
  tokens: DesignTokens;
  contrast: ContrastCheck[];
  problems?: { pointer: string; detail: string }[];
}
export interface BrandVersion {
  brand_id: string;
  version: number;
  published_at: string;
  published_by: string;
  theme_url: string;
  tokens_url: string;
}
export type FileMediaType = "image/png" | "image/jpeg" | "image/webp" | "image/avif" | "image/svg+xml" | "font/woff2";
export interface FileRecord {
  id: string;
  organization_id: string;
  sha256: string;
  name: string;
  media_type: FileMediaType;
  size: number;
  width?: number;
  height?: number;
  font?: { family: string; style: "normal" | "italic"; weight: number; weight_range: [number, number] };
  licence_confirmed: boolean;
  visibility: "private" | "public";
  url?: string;
  created_at: string;
  deleted_at?: string;
}
export interface FontFamily {
  id: string;
  family: string;
  category: string;
  use: string;
  licence: string;
  weights: { min: number; max: number };
  styles: string[];
  package: string;
  files: { subset: string; style: string; url: string; unicode_range: string }[];
}

export const IMAGE_TYPES: FileMediaType[] = ["image/png", "image/jpeg", "image/webp", "image/avif", "image/svg+xml"];
export const FONT_TYPES: FileMediaType[] = ["font/woff2"];

/** What the Foundation uses when the source leaves typography out (BrandSource's description). */
export const DEFAULT_TYPOGRAPHY: { heading: Typeface; body: Typeface; scale: Scale; base_size: number } = {
  heading: { family: "inter", weights: [700] },
  body: { family: "inter", weights: [400, 700] },
  scale: "major-third",
  base_size: 16,
};

export const brandsPath = (org: string) => `/v1/organizations/${org}/brands`;
export const brandPath = (org: string, brand: string) => `${brandsPath(org)}/${brand}`;
export const filesPath = (org: string) => `/v1/organizations/${org}/files`;
export const fileContentPath = (org: string, file: string) => `${filesPath(org)}/${file}/content`;

export const overrideKey = (name: ColourName, step: Step) => `${name}.${step}`;

/** The override slot a contrast check or a refusal points at ("/source/overrides/primary.600" → "primary.600"). */
export function overrideFromPointer(pointer: string | undefined): string | null {
  const found = /^\/source\/overrides\/((?:primary|secondary|accent|neutral|success|warning|danger)\.(?:50|[1-9]00|950))$/.exec(pointer ?? "");
  return found?.[1] ?? null;
}

/** A colour's 11 generated steps (overrides included), from the draft's tokens. */
export function scaleOf(tokens: DesignTokens, name: ColourName): { step: Step; hex: string }[] {
  return STEPS.map((step) => ({ step, hex: tokens.color[name]?.[step]?.$value.hex ?? "" }));
}

export const failing = (checks: ContrastCheck[]) => checks.filter((check) => !check.passes);

/** Every uploaded file the draft names (its logos and its uploaded fonts), each once, sorted. */
export function fileIdsOf(source: BrandSource): string[] {
  const ids = new Set<string>();
  for (const id of Object.values(source.logos ?? {})) if (id) ids.add(id);
  for (const face of [source.typography?.heading, source.typography?.body]) if (face?.family.startsWith("fil_")) ids.add(face.family);
  return [...ids].sort();
}

/** A brand's ETag as getBrand answers it, or "rev-<revision>" when the header could not be read. */
export const brandEtag = (etag: string | null | undefined, brand: BrandSummary) => etag ?? `"rev-${brand.revision}"`;

export const nextVersion = (brand: BrandSummary) => (brand.current_version ?? 0) + 1;

/** A problem's kind: the last segment of its type URI ("https://errors.emdur.com/brand-contrast" → "brand-contrast"). */
export const problemKind = (type: string) => type.slice(type.lastIndexOf("/") + 1);

/** The weights a family offers, in hundreds, within its range. */
export function weightsWithin(min: number, max: number): number[] {
  const found: number[] = [];
  for (let weight = Math.ceil(min / 100) * 100; weight <= max; weight += 100) found.push(weight);
  return found;
}
